import React, { forwardRef, PropsWithRef } from 'react';
import classnames from 'classnames';
import { Icon, IconProps } from '../icon';
import { Loader, LoaderProps } from '../loader';
import { Size } from '../types';

import styles from './text-button.module.scss';

type LoadingType = { loading?: { isLoading: boolean } & Omit<LoaderProps, 'size'> };

type HTMLButtonProps = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'id' | 'type' | 'disabled' | 'onClick' | 'className' | 'autoFocus' | 'onContextMenu'
>;

export type TextButtonProps = PropsWithRef<
  {
    variant: 'primary' | 'secondary' | 'hyperlink' | 'ghost' | 'secondary-on-media' | 'primary-lime' | 'primary-brabus';
    children: React.ReactNode;
    size?: Size;
    iconPosition?: 'left' | 'right';
    iconName?: IconProps['name'];
    isFullWidth?: boolean;
  } & HTMLButtonProps &
    LoadingType
>;

const baseClass = 'text-button';

export const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      id,
      variant = 'primary',
      size = '300',
      type = 'button',
      iconPosition = 'left',
      iconName,
      disabled,
      className,
      onClick,
      children,
      isFullWidth,
      loading,
      autoFocus,
      onContextMenu
    },
    ref
  ) => {
    const classes = classnames(
      styles[baseClass],
      styles[`${baseClass}--${variant}`],
      { [styles[`${baseClass}--${size}`]]: size },
      { [styles[`${baseClass}--full-width`]]: isFullWidth },
      { [styles[`${baseClass}--icon-${iconPosition}`]]: iconPosition },
      className
    );

    const getIcon = () => {
      if (loading?.isLoading) {
        return (
          <Loader
            size="small"
            hiddenLoadingStatus={loading.hiddenLoadingStatus}
            messageId={loading.messageId}
            className={styles[`${baseClass}__loader`]}
          />
        );
      }

      if (iconName) {
        return <Icon name={iconName} className={styles[`${baseClass}__icon`]} aria-hidden />;
      }
    };

    return (
      <button
        ref={ref}
        id={id}
        data-testid="button-component"
        className={classes}
        type={type}
        disabled={loading?.isLoading ? true : disabled}
        onClick={onClick}
        autoFocus={autoFocus}
        onContextMenu={onContextMenu}
      >
        {getIcon()}
        <span>{children}</span>
      </button>
    );
  }
);

TextButton.displayName = 'TextButton';
